import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, AlertCircle, Plus, Pencil, Trash2, X, Car, Calendar, Shield, User, CreditCard } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import SearchableSelect from './SearchableSelect';
import HeaderFilter from './HeaderFilter';
import TplIssueModal from './TplIssueModal';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Period values must match the backend/DB format (keys), same as the user page.
const PERIODS = ['days_15', 'days_30', 'days_90', 'year_1'];
// Russia insurance is only ever a 1-year policy (RUSSIA_PERIOD_CHOICES on the backend).
const RU_PERIODS = ['year_1'];

const EMPTY = {
  plate_number: '', insurance_ge: '', insurance_ru: '', insurance_am: '',
  russia_insurance_amount: '', start_date: '', is_signed: false, is_paid: false,
};

function authHeaders(json = true) {
  const token = localStorage.getItem('auth_token');
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

const inp = 'block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export default function AdminPreorders() {
  const { t } = useLanguage();
  const [rows, setRows]         = useState([]);
  const [users, setUsers]       = useState([]);
  const [selUser, setSelUser]   = useState('');
  const [plate, setPlate]       = useState('');
  const [signedFilter, setSignedFilter] = useState(''); // '', 'yes', 'no'
  const [paidFilter, setPaidFilter]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [createUser, setCreateUser]       = useState('');
  const [createVehicles, setCreateVehicles] = useState([]);
  const [tplRow, setTplRow]     = useState(null);
  const [tplPolicies, setTplPolicies] = useState([]);
  const [tplPoliciesLoading, setTplPoliciesLoading] = useState(false);
  const [tplPoliciesError, setTplPoliciesError] = useState('');
  const [checkingId, setCheckingId] = useState(null);
  const [firmCounts, setFirmCounts] = useState([]);
  const [firmModal, setFirmModal] = useState(null);   // { id, name, is_georgia }
  const [firmDocs, setFirmDocs] = useState([]);
  const [firmRecipients, setFirmRecipients] = useState([]);
  const [firmDocsLoading, setFirmDocsLoading] = useState(false);
  const [sendingFirm, setSendingFirm] = useState(false);

  const fetchFirmCounts = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/accounting/firm-counts/`, { headers: authHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setFirmCounts(data.firms || []);
      }
    } catch {}
  }, []);

  const openFirmDocs = async (fc) => {
    setFirmModal(fc);
    setFirmDocs([]);
    setFirmRecipients([]);
    setFirmDocsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/accounting/firms/${fc.id}/other-documents/`, { headers: authHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setFirmDocs(data.documents || []);
        setFirmRecipients(data.recipients || []);
      }
    } catch {}
    finally { setFirmDocsLoading(false); }
  };

  const sendFirmDocs = async () => {
    if (!firmModal) return;
    setSendingFirm(true);
    try {
      const res = await fetch(`${BASE_URL}/accounting/firms/${firmModal.id}/other-documents/`, {
        method: 'POST', headers: authHeaders(),
      });
      const r = await res.json().catch(() => ({}));
      if (r.reason === 'no_recipients') alert('No active accountants with an email address.');
      else if (r.reason === 'no_documents') alert('No documents to send.');
      else alert(`Sent ${r.sent} document(s) in ${r.batches} email(s). Failed emails: ${r.failed_batches}.`);
      fetchFirmCounts();
      setFirmModal(null);
    } catch { alert('Send failed.'); }
    finally { setSendingFirm(false); }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/users/`, { headers: authHeaders(false) });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  const fetchData = useCallback(async (uid, p, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (uid) params.set('created_by', uid);
      if (p)   params.set('plate_number', p);
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/?${params}`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(Array.isArray(data) ? data : (data.results || []));
      if (!silent) setCurrentPage(1);
    } catch { if (!silent) setError('Failed to load pre-orders.'); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); fetchFirmCounts(); }, [fetchUsers, fetchFirmCounts]);
  useEffect(() => { fetchData(selUser, ''); }, [selUser, fetchData]);

  // Live table: silently re-fetch every 15s. Paused while a modal is open
  // (create/edit, TPL issue, delete confirm) to avoid disrupting the user.
  useEffect(() => {
    const id = setInterval(() => {
      if (!modal && !tplRow && !deleteId) { fetchData(selUser, '', true); fetchFirmCounts(); }
    }, 15000);
    return () => clearInterval(id);
  }, [selUser, modal, tplRow, deleteId, fetchData, fetchFirmCounts]);

  // Normalize legacy period strings ('1 year', '90 days') to the key format
  // used by the selects/backend so they pre-select correctly on edit.
  const normalizePeriod = (v) => ({
    '1 year': 'year_1', '90 days': 'days_90', '30 days': 'days_30', '15 days': 'days_15',
  }[v] || v || '');

  const loadAllVehicles = async () => {
    setCreateVehicles([]);
    try {
      const res = await fetch(`${BASE_URL}/vehicles/admin-vehicles/`, { headers: authHeaders(false) });
      if (res.ok) setCreateVehicles(await res.json());
    } catch {}
  };

  const usernameById = (id) => users.find(u => String(u.id) === String(id))?.username || '';

  // Real tpl.ge policies for the selected vehicle (shown in the create modal).
  const loadTplPolicies = async (plate) => {
    setTplPolicies([]);
    setTplPoliciesError('');
    if (!plate) return;
    setTplPoliciesLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/tpl-policies-by-plate/${encodeURIComponent(plate)}/`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTplPolicies(data.policies || []);
    } catch {
      setTplPoliciesError('Failed to load tpl.ge policies.');
    }
    finally { setTplPoliciesLoading(false); }
  };

  // Selecting a period for one country clears the others (exactly one type per pre-order).
  const setSingleType = (country, value) => {
    setForm(prev => ({
      ...prev,
      insurance_ge: country === 'ge' ? value : '',
      insurance_ru: country === 'ru' ? value : '',
      insurance_am: country === 'am' ? value : '',
      // reset russia amount if RU is cleared
      russia_insurance_amount: country === 'ru' && value ? prev.russia_insurance_amount : '',
    }));
  };

  const openCreate = () => {
    setForm(EMPTY);
    setCreateUser('');
    setTplPolicies([]);
    loadAllVehicles();
    setModal({ mode: 'create' });
  };
  const openEdit   = (r) => {
    setForm({
      ...EMPTY, ...r,
      insurance_ge: normalizePeriod(r.insurance_ge),
      insurance_ru: normalizePeriod(r.insurance_ru),
      insurance_am: normalizePeriod(r.insurance_am),
      russia_insurance_amount: r.russia_insurance_amount || '',
    });
    setModal({ mode: 'edit', id: r.id });
  };

  const handleSave = async () => {
    const isEdit = modal.mode === 'edit';
    // ── Validation (mirrors the backend / Django admin form) ──
    if (!isEdit && !form.plate_number) { alert('Please select a vehicle.'); return; }
    if (!form.start_date) { alert('Please provide a start date.'); return; }
    const selectedTypes = [form.insurance_ge, form.insurance_ru, form.insurance_am].filter(Boolean);
    if (selectedTypes.length === 0) { alert('Please select one insurance type (Georgia, Russia, or Armenia).'); return; }
    if (selectedTypes.length > 1) { alert('Only one insurance type can be selected per pre-order.'); return; }
    if (form.insurance_ru && form.is_signed && !form.russia_insurance_amount) {
      alert('Russia insurance amount is required when Russia insurance is signed.'); return;
    }

    setSaving(true);
    try {
      const url    = isEdit ? `${BASE_URL}/pre-ordered-insurances/${modal.id}/` : `${BASE_URL}/pre-ordered-insurances/`;
      // On create, tie the pre-order to the selected vehicle's owner.
      const payload = isEdit ? form : { ...form, created_by: createUser || undefined };
      const res = await fetch(url, { method: isEdit ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text());
      setModal(null);
      fetchData(selUser, plate);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleCheckPayment = async (r) => {
    setCheckingId(r.id);
    try {
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/${r.id}/check-payment/`, { method: 'POST', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      alert(data.message || (res.ok ? 'Checked.' : 'Check failed.'));
      fetchData(selUser, plate);
    } catch { alert('Check failed.'); }
    finally { setCheckingId(null); }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/pre-ordered-insurances/${id}/`, { method: 'DELETE', headers: authHeaders(false) });
      if (!res.ok && res.status !== 204) throw new Error();
      setDeleteId(null);
      fetchData(selUser, plate);
    } catch { alert('Failed to delete.'); }
  };

  const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const filteredRows = rows.filter(r =>
    (signedFilter === '' || (signedFilter === 'yes' ? r.is_signed : !r.is_signed)) &&
    (paidFilter === '' || (paidFilter === 'yes' ? r.is_paid : !r.is_paid))
  );
  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const paged = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">{t('orderedInsurances')}</h1>
        <div className="flex gap-2">
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={15}/> Add
          </button>
          <button onClick={() => fetchData(selUser, plate)} disabled={loading} className="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('refresh')}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={selUser} onChange={e => setSelUser(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
          <option value="">All users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
        </select>
        <form onSubmit={e => { e.preventDefault(); fetchData(selUser, plate); }} className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input type="text" placeholder="Search plate…" value={plate} onChange={e => setPlate(e.target.value)}
              className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"/>
          </div>
          <button type="submit" className="px-3 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700">Search</button>
        </form>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle size={16}/>{error}
        </div>
      )}

      {/* Per-firm unsent TPL GE invoice counters (click action TBD) */}
      <div className="flex flex-wrap gap-3">
        {firmCounts.map(fc => (
          <button
            key={fc.id}
            onClick={() => openFirmDocs(fc)}
            title={t('unsentTplInvoices')}
            className={`flex flex-col items-start gap-0.5 px-4 py-2 rounded-lg border shadow-sm transition-colors ${
              fc.is_georgia
                ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                : 'bg-violet-50 border-violet-200 hover:bg-violet-100'
            }`}
          >
            <span className={`text-[11px] font-medium uppercase tracking-wide ${fc.is_georgia ? 'text-emerald-600' : 'text-violet-600'}`}>
              {t('tplGeInvoices')}
            </span>
            <span className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${fc.is_georgia ? 'text-emerald-800' : 'text-violet-800'}`}>
                {fc.name}
              </span>
              <span className={`min-w-[1.5rem] text-center text-sm font-bold px-2 py-0.5 rounded-full text-white ${
                fc.is_georgia ? 'bg-emerald-600' : 'bg-violet-600'
              }`}>
                {fc.unsent_count}
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  { label: 'Plate' },
                  { label: t('insuranceGE') },
                  { label: t('insuranceRU') },
                  { label: t('insuranceAM') },
                  { label: t('startDate') },
                  { label: t('signed'), filter: 'signed' },
                  { label: t('paid'), filter: 'paid' },
                  { label: 'Created by' },
                  { label: 'Ordered' },
                  { label: t('actions') },
                ].map(h => (
                  <th key={h.label} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {h.label}
                    {h.filter && (
                      <HeaderFilter
                        value={h.filter === 'signed' ? signedFilter : paidFilter}
                        onChange={(v) => { (h.filter === 'signed' ? setSignedFilter : setPaidFilter)(v); setCurrentPage(1); }}
                        options={[{ value: '', label: 'All' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? [...Array(6)].map((_, i) => (
                <tr key={i}>{[...Array(10)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td>)}</tr>
              )) : filteredRows.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-10 text-gray-400">No pre-orders found</td></tr>
              ) : paged.map(r => (
                <tr key={r.id} className={`hover:bg-primary-50 ${r.is_signed ? '' : (r.tpl_ge_uid ? 'bg-blue-100' : 'bg-yellow-50')}`}>
                  <td className="px-4 py-3 font-medium text-gray-800">{r.plate_number}</td>
                  <td className="px-4 py-3 text-gray-600">{r.insurance_ge ? t(r.insurance_ge) : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{r.insurance_ru ? t(r.insurance_ru) : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{r.insurance_am ? t(r.insurance_am) : '—'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.start_date || '—'}</td>
                  <td className="px-4 py-3">
                    {r.is_signed ? (
                      r.tpl_ge_invoice ? (
                        <a href={r.tpl_ge_invoice} target="_blank" rel="noopener noreferrer"
                          title="Open attached invoice"
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 inline-flex items-center gap-1">
                          Yes ↗
                        </a>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Yes</span>
                      )
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {r.is_paid ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.created_by || '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {r.ordered_date ? new Date(r.ordered_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {(r.is_signed || r.is_paid) ? (
                      <span className="text-xs text-gray-400" title="Signed or paid pre-orders are locked">🔒 Locked</span>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex gap-1">
                          {r.insurance_ge && (
                            <button onClick={() => setTplRow(r)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Issue TPL"><Shield size={19}/></button>
                          )}
                          {r.insurance_ge && r.tpl_ge_uid && (
                            <button onClick={() => handleCheckPayment(r)} disabled={checkingId === r.id}
                              className="p-1.5 rounded hover:bg-yellow-50 text-yellow-600 disabled:opacity-40" title="Check payment">
                              <CreditCard size={19} className={checkingId === r.id ? 'animate-pulse' : ''}/>
                            </button>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-primary-50 text-primary-600" title="Edit"><Pencil size={14}/></button>
                          <button onClick={() => setDeleteId(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={14}/></button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filteredRows.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredRows.length)} / {filteredRows.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
        {!loading && filteredRows.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <h2 className="text-xl font-semibold">{modal.mode === 'create' ? 'New Pre-order' : 'Edit Pre-order'}</h2>
              <button onClick={() => setModal(null)} className="p-2 text-white hover:text-gray-200 rounded-full transition duration-150"><X size={18}/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* User + Vehicle (create): pick vehicle → plate + owner auto-fill.
                  Select user first to narrow vehicles, or pick a vehicle directly. */}
              {modal.mode === 'create' && (
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <SearchableSelect
                      icon={<User className="h-5 w-5 text-gray-400" />}
                      placeholder="— Select user —"
                      value={createUser}
                      options={[{ value: '', label: 'All users' }, ...users.map(u => ({ value: u.id, label: u.username }))]}
                      onChange={(uid) => { setCreateUser(uid); setForm(prev => ({ ...prev, plate_number: '' })); setTplPolicies([]); }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle *</label>
                    <SearchableSelect
                      icon={<Car className="h-5 w-5 text-gray-400" />}
                      placeholder="— Select vehicle —"
                      value={createVehicles.find(v => v.plate_number === form.plate_number && (!createUser || String(v.created_by_id) === String(createUser)))?.id || ''}
                      options={createVehicles
                        .filter(v => !createUser || String(v.created_by_id) === String(createUser))
                        .map(v => ({ value: v.id, label: createUser ? v.plate_number : `${v.plate_number}${usernameById(v.created_by_id) ? ' — ' + usernameById(v.created_by_id) : ''}` }))}
                      onChange={(vid) => {
                        const veh = createVehicles.find(v => String(v.id) === String(vid));
                        setCreateUser(veh?.created_by_id || createUser);
                        setForm(prev => ({ ...prev, plate_number: veh?.plate_number || '' }));
                        loadTplPolicies(veh?.plate_number);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Existing tpl.ge policies for the selected vehicle (create) */}
              {modal.mode === 'create' && form.plate_number && (
                <div className={`mb-6 p-3 border rounded-md ${tplPoliciesError ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
                  {tplPoliciesLoading ? (
                    <div className="text-sm text-blue-700 flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Loading tpl.ge policies…</div>
                  ) : tplPoliciesError ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-orange-800">{tplPoliciesError}</div>
                      <button type="button" onClick={() => loadTplPolicies(form.plate_number)}
                        className="px-3 py-1.5 bg-orange-600 text-white rounded-md text-sm hover:bg-orange-700 whitespace-nowrap flex items-center gap-1.5">
                        <RefreshCw className="h-3.5 w-3.5" /> Retry
                      </button>
                    </div>
                  ) : tplPolicies.length > 0 ? (
                    <>
                      <div className="text-sm font-medium text-blue-800 mb-2">
                        {tplPolicies.length} existing tpl.ge {tplPolicies.length === 1 ? 'policy' : 'policies'} for {form.plate_number}
                      </div>
                      <div className="max-h-28 overflow-y-auto space-y-1">
                        {tplPolicies.slice(0, 5).map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs text-gray-600 gap-2">
                            <span className="font-medium">{p.policyNumber || '—'}</span>
                            <span className="whitespace-nowrap">{(p.startDate || '?').slice(0, 10)} — {(p.endDate || '?').slice(0, 10)}</span>
                            <span className={`px-1.5 py-0.5 rounded ${p.policyActiveStatus === 'A' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                              {p.policyActiveStatus === 'A' ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-blue-700">No tpl.ge policies found for {form.plate_number}.</div>
                  )}
                </div>
              )}

              {/* Plate & Date */}
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Plate only in edit mode — in create the Vehicle selector already shows it */}
                  {modal.mode === 'edit' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plate number</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Car className="h-5 w-5 text-gray-400" /></div>
                        <input value={form.plate_number} readOnly
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700" />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('startDate')} *</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Calendar className="h-5 w-5 text-gray-400" /></div>
                      <input type="date" value={form.start_date} onChange={f('start_date')}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance period — exactly one type (GE / RU / AM) per pre-order */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-gray-500" /> Insurance Period <span className="text-xs font-normal text-gray-400">(one type only)</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceGE')}</label>
                    <select value={form.insurance_ge} onChange={e => setSingleType('ge', e.target.value)} className={inp}>
                      <option value="">— None —</option>
                      {PERIODS.map(p => <option key={p} value={p}>{t(p)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceRU')}</label>
                    <select value={form.insurance_ru} onChange={e => setSingleType('ru', e.target.value)} className={inp}>
                      <option value="">— None —</option>
                      {RU_PERIODS.map(p => <option key={p} value={p}>{t(p)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('insuranceAM')}</label>
                    <select value={form.insurance_am} onChange={e => setSingleType('am', e.target.value)} className={inp}>
                      <option value="">— None —</option>
                      {PERIODS.map(p => <option key={p} value={p}>{t(p)}</option>)}
                    </select>
                  </div>
                </div>

                {/* Russia insurance amount — required when RU is signed */}
                {form.insurance_ru && (
                  <div className="mt-4 max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Russia insurance amount (RUB){form.is_signed ? ' *' : ''}
                    </label>
                    <input type="number" min="0" step="0.01" value={form.russia_insurance_amount} onChange={f('russia_insurance_amount')}
                      className={inp} placeholder="0.00" />
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-800 mb-4 pb-2 border-b border-gray-200">{t('status')}</h3>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={form.is_signed} onChange={f('is_signed')}
                      className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    {t('signed')}
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={form.is_paid} onChange={f('is_paid')}
                      className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                    {t('paid')}
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">{t('cancel')}</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50">
                {saving ? 'Saving…' : (modal.mode === 'create' ? t('save') : t('update'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden w-80">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <h3 className="font-semibold">{t('delete')}</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">Delete this pre-order? This cannot be undone.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50">{t('cancel') || 'Cancel'}</button>
                <button onClick={() => handleDelete(deleteId)} className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700">{t('delete')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tplRow && (
        <TplIssueModal
          preorder={tplRow}
          defaultVehicleType={createVehicles.find(v => v.plate_number === tplRow.plate_number)?.type === 'semi_trailer' ? 'trailer' : 'truck'}
          onClose={() => setTplRow(null)}
          onIssued={() => fetchData(selUser, plate)}
        />
      )}

      {firmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
              <div>
                <h2 className="text-lg font-semibold">{t('unsentTplInvoices')}</h2>
                <p className="text-xs text-gray-300">{firmModal.name}</p>
              </div>
              <button onClick={() => setFirmModal(null)} className="p-2 text-white hover:text-gray-200 rounded-full"><X size={18}/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {firmRecipients.length > 0 ? (
                <p className="text-xs text-gray-500 mb-3">
                  {t('recipients') || 'Recipients'}: {firmRecipients.join(', ')}
                </p>
              ) : (
                <p className="text-xs text-red-600 mb-3">No active accountants with an email address.</p>
              )}

              {firmDocsLoading ? (
                <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin"/> Loading…</div>
              ) : firmDocs.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No unsent documents</div>
              ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md">
                  {firmDocs.map(d => (
                    <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="text-gray-700 truncate">{d.name}</span>
                      <span className="flex items-center gap-3 whitespace-nowrap">
                        <span className="text-xs text-gray-400">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}</span>
                        {d.file && <a href={d.file} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">Open ↗</a>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setFirmModal(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">{t('cancel')}</button>
              <button onClick={sendFirmDocs} disabled={sendingFirm || firmDocs.length === 0 || firmRecipients.length === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
                {sendingFirm ? 'Sending…' : (t('send') || 'Send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
